import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { mainSiteUrl, categories } from '@/mocks/categories';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shopGuideOpen, setShopGuideOpen] = useState(false);
  const location = useLocation();
  const shopRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (shopRef.current && !shopRef.current.contains(event.target as Node)) {
        setShopGuideOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const articleLinks = [
    { label: 'Ultimate Spreadsheet Guide', to: '/article/sugargoo-spreadsheet-guide' },
    { label: 'Shipping Cost Explained', to: '/article/sugargoo-shipping-cost' },
    { label: 'Is Sugargoo Legit? Review', to: '/article/is-sugargoo-legit' },
    { label: 'Best Spreadsheet Resources', to: '/article/best-sugargoo-spreadsheet-resources' },
    { label: 'How to Use Spreadsheets', to: '/article/how-to-use-sugargoo-spreadsheet' },
    { label: 'Beginner Starter Guide', to: '/article/sugargoo-spreadsheet-for-beginners' },
    { label: 'Coupon & Discount Guide', to: '/article/sugargoo-coupon-discount-guide' },
    { label: 'Warehouse Guide', to: '/article/sugargoo-warehouse-guide' },
    { label: 'Product Categories', to: '/article/sugargoo-spreadsheet-categories' },
    { label: 'Spreadsheet vs Manual Search', to: '/article/sugargoo-spreadsheet-vs-manual-search' },
  ];

  return (
    <nav className="w-full bg-white/95 backdrop-blur-sm border-b border-background-200 sticky top-0 z-50">
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-14 md:h-16">
          <Link to="/" className="font-heading font-bold text-foreground-950 text-lg hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            Sugargoo Sheet
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                location.pathname === '/'
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
              }`}
            >
              <i className="ri-home-4-line mr-1.5"></i>Home
            </Link>

            <div className="relative" ref={shopRef}>
              <button
                onClick={() => setShopGuideOpen(!shopGuideOpen)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer flex items-center gap-1 ${
                  location.pathname.startsWith('/article')
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                }`}
              >
                <i className="ri-book-open-line mr-1.5"></i>Shopping Guide
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${shopGuideOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {shopGuideOpen && (
                <div className="absolute top-full mt-2 left-0 w-72 bg-white border border-background-200 rounded-xl shadow-lg overflow-hidden animate-scale-in z-50">
                  <div className="max-h-[420px] overflow-y-auto py-2">
                    {articleLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        onClick={() => setShopGuideOpen(false)}
                        className="block w-full text-left px-4 py-2.5 text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-700 cursor-pointer transition-colors"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap ml-2 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-all cursor-pointer"
            >
              <i className="ri-shopping-bag-3-line mr-1.5"></i>Visit Store
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <i className="ri-close-line text-xl"></i>
            ) : (
              <i className="ri-menu-line text-xl"></i>
            )}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200 bg-white animate-slide-down max-h-[calc(100vh-3.5rem)] overflow-y-auto overscroll-contain">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className={`block w-full px-4 py-3 rounded-xl text-sm font-medium transition-colors cursor-pointer ${
                location.pathname === '/'
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-foreground-600 hover:bg-background-100'
              }`}
            >
              <i className="ri-home-4-line mr-2"></i>Home
            </Link>

            <div className="pt-2 pb-1">
              <span className="px-4 text-xs font-semibold text-foreground-400 uppercase tracking-wide">
                Shopping Guide Articles
              </span>
            </div>
            {articleLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={`block w-full px-4 py-2.5 rounded-xl text-sm transition-colors cursor-pointer ${
                  location.pathname === link.to
                    ? 'bg-primary-50 text-primary-700 font-medium'
                    : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                <i className="ri-article-line mr-2 text-foreground-400"></i>{link.label}
              </Link>
            ))}

            <div className="pt-2 pb-1">
              <span className="px-4 text-xs font-semibold text-foreground-400 uppercase tracking-wide">
                Product Categories
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="block px-3 py-2.5 rounded-lg text-xs text-foreground-600 hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer truncate"
                >
                  {cat.name}
                </a>
              ))}
            </div>

            <div className="pt-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="block w-full text-center px-4 py-3 bg-primary-500 text-white text-sm font-medium rounded-xl hover:bg-primary-600 transition-all cursor-pointer"
              >
                <i className="ri-shopping-bag-3-line mr-1.5"></i>Visit Main Store
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}